import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useTranslation } from "react-i18next";
import { listAgentHistory } from "@/lib/agents.functions";
import { STUDIO_AGENTS } from "@/lib/ai/_agents-studio";
import { usePlan } from "@/hooks/use-plan";
import { useLocaleFmt } from "@/lib/i18n/format";
import { Loader2, Search } from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/agenti/storico")({
  component: HistoryPage,
});

function HistoryPage() {
  const { t } = useTranslation();
  const { localeTag } = useLocaleFmt();
  const { plan } = usePlan();
  const fetchHistory = useServerFn(listAgentHistory);
  const [agentId, setAgentId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [search, setSearch] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["agent-history", agentId, from, to, search],
    queryFn: () => fetchHistory({ data: {
      agentId: agentId || undefined,
      from: from ? new Date(from).toISOString() : undefined,
      to: to ? new Date(to + "T23:59:59").toISOString() : undefined,
      search: search || undefined,
    } }),
    enabled: plan === "studio",
  });

  if (plan !== "studio") {
    return <div className="p-8 text-center text-muted-foreground">{t("pages.agenti.histOnly")}</div>;
  }

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-8">
      <h1 className="text-3xl font-bold text-foreground">{t("pages.agenti.histTitle")}</h1>
      <p className="mt-2 text-muted-foreground">{t("pages.agenti.histSubtitle")}</p>

      <div className="mt-6 grid gap-3 rounded-2xl border-2 border-border-strong bg-background p-4 sm:grid-cols-4">
        <div>
          <label className="block text-[12px] font-semibold uppercase text-muted-foreground">{t("pages.agenti.histAgent")}</label>
          <select value={agentId} onChange={(e) => setAgentId(e.target.value)} className="mt-1 w-full rounded-lg border-2 border-border-strong bg-background px-3 py-2 text-[14px]">
            <option value="">{t("pages.agenti.histAll")}</option>
            {STUDIO_AGENTS.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[12px] font-semibold uppercase text-muted-foreground">{t("pages.agenti.histFrom")}</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 w-full rounded-lg border-2 border-border-strong bg-background px-3 py-2 text-[14px]" />
        </div>
        <div>
          <label className="block text-[12px] font-semibold uppercase text-muted-foreground">{t("pages.agenti.histTo")}</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 w-full rounded-lg border-2 border-border-strong bg-background px-3 py-2 text-[14px]" />
        </div>
        <div>
          <label className="block text-[12px] font-semibold uppercase text-muted-foreground">{t("pages.agenti.histSearch")}</label>
          <div className="relative mt-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("pages.agenti.histKeyword")} className="w-full rounded-lg border-2 border-border-strong bg-background pl-9 pr-3 py-2 text-[14px]" />
          </div>
        </div>
      </div>

      <div className="mt-6 space-y-3">
        {isLoading && <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" /></div>}
        {!isLoading && (data ?? []).length === 0 && (
          <div className="rounded-2xl border-2 border-dashed border-border-strong p-8 text-center text-muted-foreground">{t("pages.agenti.histNone")}</div>
        )}
        {(data ?? []).map((c: any) => {
          const agent = STUDIO_AGENTS.find((a) => a.id === c.agent_id);
          return (
            <Link key={c.id} to="/app" className="block rounded-2xl border-2 border-border-strong bg-background p-4 hover:border-primary">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-bold uppercase text-primary">{agent?.name ?? c.agent_id}</span>
                    {c.matter?.clients?.name && (
                      <span className="text-[12px] text-muted-foreground">· {c.matter.clients.name}</span>
                    )}
                  </div>
                  <h3 className="mt-1 truncate text-[15px] font-semibold text-foreground">{c.title}</h3>
                  <p className="mt-1 line-clamp-2 text-[13px] text-muted-foreground">{c.preview}</p>
                </div>
                <span className="shrink-0 text-[12px] text-muted-foreground">
                  {new Date(c.created_at).toLocaleDateString(localeTag, { day: "2-digit", month: "short", year: "numeric" })}
                </span>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
